import { motion } from "framer-motion";
import { Aperture, Lock, CreditCard } from "lucide-react";

// CSS-stylized device mockups with brand glow. Photos inserted via object-cover.

export const PhoneMockup = ({ image, title = "iFotoLife", className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 40, rotateY: -8 }}
    animate={{ opacity: 1, y: 0, rotateY: 0 }}
    transition={{ duration: 0.9, ease: [0.85, 0, 0.15, 1] }}
    className={`relative mx-auto w-[220px] sm:w-[260px] ${className}`}
    style={{ perspective: 1000 }}
    data-testid="phone-mockup"
  >
    <div
      className="relative aspect-[9/19] rounded-[42px] border border-white/15 bg-navy-900 p-2.5"
      style={{ boxShadow: "0 30px 80px -20px rgba(0,0,0,0.7), 0 0 60px rgba(212,178,106,0.12)" }}
    >
      <div className="absolute left-1/2 top-2.5 z-20 h-5 w-24 -translate-x-1/2 rounded-full bg-navy-900 border border-white/10" />
      <div className="relative h-full w-full overflow-hidden rounded-[34px]">
        <img src={image} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-900/90 via-navy-900/10 to-navy-900/40" />
        <div className="absolute left-0 right-0 top-8 flex items-center justify-center gap-1.5">
          <Aperture className="h-3.5 w-3.5 text-champagne" strokeWidth={1.5} />
          <span className="font-serif text-sm text-cream">{title}</span>
        </div>
        <div className="absolute bottom-4 left-4 right-4">
          <div className="mb-2 h-1.5 w-16 rounded-full bg-champagne" />
          <div className="grid grid-cols-3 gap-1.5">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="aspect-square rounded-md bg-white/15 backdrop-blur-sm" />
            ))}
          </div>
        </div>
      </div>
    </div>
  </motion.div>
);

export const DesktopMockup = ({ image, title = "iFotoLife Studio", className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.9, ease: [0.85, 0, 0.15, 1] }}
    className={`relative mx-auto w-full max-w-[520px] ${className}`}
    data-testid="desktop-mockup"
  >
    <div
      className="relative overflow-hidden rounded-2xl border border-white/15 bg-navy-900"
      style={{ boxShadow: "0 30px 80px -20px rgba(0,0,0,0.7), 0 0 60px rgba(212,178,106,0.10)" }}
    >
      <div className="flex items-center gap-1.5 border-b border-white/10 bg-navy-700/60 px-4 py-2.5">
        <span className="h-2.5 w-2.5 rounded-full bg-coral/80" />
        <span className="h-2.5 w-2.5 rounded-full bg-champagne/80" />
        <span className="h-2.5 w-2.5 rounded-full bg-white/30" />
        <span className="ml-3 font-serif text-xs text-cream/80">{title}</span>
      </div>
      <div className="relative aspect-[16/10]">
        <img src={image} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-900/80 to-transparent" />
        <div className="absolute bottom-4 left-4 right-4 grid grid-cols-4 gap-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="aspect-square rounded-lg bg-white/15 backdrop-blur-sm" />
          ))}
        </div>
      </div>
    </div>
  </motion.div>
);

export const DeviceMockup = ({ type, image, title }) =>
  type === "payment" ? (
    <PaymentMockup title={title} />
  ) : type === "desktop" ? (
    <DesktopMockup image={image} title={title} />
  ) : (
    <PhoneMockup image={image} title={title} />
  );

export const PaymentMockup = ({ title = "Paiement" }) => (
  <motion.div
    initial={{ opacity: 0, y: 40, rotateY: -8 }}
    animate={{ opacity: 1, y: 0, rotateY: 0 }}
    transition={{ duration: 0.9, ease: [0.85, 0, 0.15, 1] }}
    className="relative mx-auto w-[220px] sm:w-[260px]"
    style={{ perspective: 1000 }}
    data-testid="payment-mockup"
  >
    <div
      className="relative aspect-[9/19] rounded-[42px] border border-white/15 bg-navy-900 p-2.5"
      style={{ boxShadow: "0 30px 80px -20px rgba(0,0,0,0.7), 0 0 60px rgba(212,178,106,0.12)" }}
    >
      <div className="absolute left-1/2 top-2.5 z-20 h-5 w-24 -translate-x-1/2 rounded-full bg-navy-900 border border-white/10" />
      <div className="relative flex h-full w-full flex-col overflow-hidden rounded-[34px] bg-cream p-4 pt-9">
        <div className="mb-3 flex items-center gap-1.5">
          <Lock className="h-3.5 w-3.5 text-[#B8923f]" strokeWidth={1.8} />
          <span className="font-serif text-sm text-navy-900">{title}</span>
        </div>

        {/* Order line */}
        <div className="mb-3 flex items-center justify-between rounded-xl border border-navy-900/10 bg-white px-3 py-2.5">
          <span className="text-[11px] text-navy-900/60">Photos HD × 6</span>
          <span className="text-sm font-semibold text-navy-900">29,90 €</span>
        </div>

        {/* Card field */}
        <div className="mb-2 rounded-xl border border-navy-900/10 bg-white px-3 py-2.5">
          <div className="mb-1 text-[9px] uppercase tracking-wider text-navy-900/45">Carte</div>
          <div className="flex items-center justify-between">
            <span className="text-xs tracking-widest text-navy-900/80">4242 4242 ····</span>
            <CreditCard className="h-4 w-4 text-navy-900/50" strokeWidth={1.5} />
          </div>
        </div>

        {/* Stripe pay button */}
        <button className="mb-2 flex w-full items-center justify-center gap-1.5 rounded-xl bg-[#635BFF] py-2.5 text-xs font-semibold text-white">
          Payer · <span className="font-bold tracking-tight">stripe</span>
        </button>

        {/* Divider */}
        <div className="my-1 flex items-center gap-2">
          <span className="h-px flex-1 bg-navy-900/10" />
          <span className="text-[9px] uppercase tracking-wider text-navy-900/40">ou</span>
          <span className="h-px flex-1 bg-navy-900/10" />
        </div>

        {/* PayPal button */}
        <button className="flex w-full items-center justify-center rounded-xl bg-[#FFC439] py-2.5 text-xs font-bold italic text-[#003087]">
          Pay<span className="text-[#0070E0]">Pal</span>
        </button>

        <div className="mt-auto flex items-center justify-center gap-1 pt-3 text-[9px] text-navy-900/45">
          <Lock className="h-2.5 w-2.5" strokeWidth={2} /> Paiement 100% sécurisé
        </div>
      </div>
    </div>
  </motion.div>
);
